import { Component, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { Button } from './Button.tsx';

/**
 * One page's errors stay on that page: a thrown render error shows "This page could not load" with a Reload button
 * and the error's words (for support), instead of taking the whole portal down with it. The Shell gives it the
 * page id as its key, so moving to another page starts afresh.
 */
export interface PageBoundaryProps {
  readonly children: ReactNode;
  /** Reload the page (the browser's reload unless given, as in tests). */
  readonly onReload?: () => void;
  readonly onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  readonly error: Error | null;
}

export class PageBoundary extends Component<PageBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const reload = this.props.onReload ?? (() => window.location.reload());
    return <PageFailed error={error} onReload={reload} />;
  }
}

function PageFailed({
  error,
  onReload,
}: {
  readonly error: Error;
  readonly onReload: () => void;
}): ReactElement {
  return (
    <section className="page page--narrow" role="alert" aria-labelledby="page-failed-title">
      <div className="panel stack page-failed">
        <h1 id="page-failed-title" className="title">
          This page could not load
        </h1>
        <p className="body">
          Something went wrong while this page was drawn. The rest of the portal still works: choose another
          page from the menu, or reload this one. If it happens again, send the words below to support.
        </p>
        <div className="row">
          <Button variant="primary" onClick={onReload}>
            Reload
          </Button>
        </div>
        <p className="caption muted page-failed__words">{`${error.name}: ${error.message}`}</p>
      </div>
    </section>
  );
}
